import { Site } from "@/bryntum-lib/models/Site";

export type SitePublishDisabledReason = "no_site" | "publishing" | "not_ready" | "up_to_date" | "read_only" | null;

export interface SitePublishButtonState {
  enabled: boolean;
  reason: SitePublishDisabledReason;
  tooltip: string | null;
  hasUnpublishedChanges: boolean;
  siteCanPublish: boolean;
}

const TOOLTIP_NOT_READY_NO_CHANGES =
  "Set up your site first — add at least one included page with generated HTML.";
const TOOLTIP_NOT_READY_WITH_CHANGES = "You have saved edits, but publish isn't ready yet.";
const TOOLTIP_UP_TO_DATE = "Site is up to date.";
const TOOLTIP_PUBLISHING = "Publishing…";
const TOOLTIP_PUBLISH_LIVE_NO_PREVIEW = "Preview your site first, or save changes to publish live.";

function readSiteCanPublish(site: Site | null | undefined): boolean {
  return site?.siteCanPublish === true;
}

function readHasUnpublishedChanges(site: Site | null | undefined): boolean {
  return site?.hasUnpublishedChanges === true;
}

export function getSitePublishButtonState({
  site,
  publishTaskPending,
  siteReady,
  isReadOnly,
}: {
  site: Site | null | undefined;
  publishTaskPending: boolean;
  siteReady: boolean;
  isReadOnly: boolean;
}): SitePublishButtonState {
  const siteCanPublish = readSiteCanPublish(site);
  const hasUnpublishedChanges = readHasUnpublishedChanges(site);

  if (!site || !siteReady) {
    return {
      enabled: false,
      reason: "no_site",
      tooltip: TOOLTIP_NOT_READY_NO_CHANGES,
      hasUnpublishedChanges,
      siteCanPublish,
    };
  }

  if (isReadOnly) {
    return {
      enabled: false,
      reason: "read_only",
      tooltip: null,
      hasUnpublishedChanges,
      siteCanPublish,
    };
  }

  if (publishTaskPending) {
    return {
      enabled: false,
      reason: "publishing",
      tooltip: TOOLTIP_PUBLISHING,
      hasUnpublishedChanges,
      siteCanPublish,
    };
  }

  if (!siteCanPublish) {
    return {
      enabled: false,
      reason: "not_ready",
      tooltip: hasUnpublishedChanges ? TOOLTIP_NOT_READY_WITH_CHANGES : TOOLTIP_NOT_READY_NO_CHANGES,
      hasUnpublishedChanges,
      siteCanPublish,
    };
  }

  if (!hasUnpublishedChanges) {
    return {
      enabled: false,
      reason: "up_to_date",
      tooltip: TOOLTIP_UP_TO_DATE,
      hasUnpublishedChanges,
      siteCanPublish,
    };
  }

  return {
    enabled: true,
    reason: null,
    tooltip: null,
    hasUnpublishedChanges,
    siteCanPublish,
  };
}

export interface SitePublishLiveButtonState {
  enabled: boolean;
  tooltip: string | null;
}

/** Publish live can deploy an existing preview even when the editor has no new changes. */
export function getSitePublishLiveButtonState({
  site,
  publishTaskPending,
  siteReady,
  isReadOnly,
  isLive,
}: {
  site: Site | null | undefined;
  publishTaskPending: boolean;
  siteReady: boolean;
  isReadOnly: boolean;
  isLive: boolean;
}): SitePublishLiveButtonState {
  const siteCanPublish = readSiteCanPublish(site);
  const hasUnpublishedChanges = readHasUnpublishedChanges(site);

  if (!site || !siteReady) {
    return { enabled: false, tooltip: TOOLTIP_NOT_READY_NO_CHANGES };
  }

  if (isReadOnly) {
    return { enabled: false, tooltip: null };
  }

  if (publishTaskPending) {
    return { enabled: false, tooltip: TOOLTIP_PUBLISHING };
  }

  if (site.domainStatus !== "active") {
    return { enabled: false, tooltip: null };
  }

  if (!siteCanPublish) {
    return {
      enabled: false,
      tooltip: hasUnpublishedChanges ? TOOLTIP_NOT_READY_WITH_CHANGES : TOOLTIP_NOT_READY_NO_CHANGES,
    };
  }

  if (isLive) {
    return { enabled: true, tooltip: null };
  }

  if (hasUnpublishedChanges) {
    return { enabled: true, tooltip: null };
  }

  return { enabled: false, tooltip: TOOLTIP_PUBLISH_LIVE_NO_PREVIEW };
}

function pickSitePayload(data: unknown): Record<string, unknown> | null {
  if (!data || typeof data !== "object") return null;
  const record = data as Record<string, unknown>;
  if (record.site && typeof record.site === "object") {
    return record.site as Record<string, unknown>;
  }
  if ("has_unpublished_changes" in record || "can_publish" in record || "published_at" in record) {
    return record;
  }
  return null;
}

/** Merge read-only publish fields from API/async-task result into the Bryntum site row. */
export function mergeSitePublishFromApi(site: Site, data: unknown): void {
  const apiSite = pickSitePayload(data);
  if (!apiSite) return;

  const patch: Record<string, unknown> = {};

  if (apiSite.has_unpublished_changes != null) {
    patch.hasUnpublishedChanges = Boolean(apiSite.has_unpublished_changes);
  }
  if (apiSite.can_publish != null) {
    patch.siteCanPublish = Boolean(apiSite.can_publish);
  }
  if (apiSite.published_at != null) {
    patch.publishedAt = apiSite.published_at;
  }
  if (apiSite.url != null) {
    patch.url = String(apiSite.url);
  }

  if (Object.keys(patch).length > 0) {
    site.set(patch);
  }
}
